import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography, Grid, FormGroup, FormControlLabel, Checkbox, InputLabel,LinearProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {
    Search as SearchIcon,
    Info as InfoIcon,
    InfoOutlined as InfoOutlinedIcon,
    Error as ErrorIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Code as CodeIcon,
} from '@material-ui/icons';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: '#1e1e28',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
        },
    },
    searchForm: {
        display: 'flex',
        marginBottom: theme.spacing(3),
        alignItems: 'center',
        width: '100%',
    },
    searchField: {
        flex: '1 1 auto',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#252530',
            '& fieldset': {
                borderColor: '#444',
                transition: 'border-color 0.3s',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
        '& .MuiOutlinedInput-input': {
            color: '#fff',
            padding: '16px',
        },
        '& .MuiInputLabel-outlined': {
            color: '#888',
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
    },
    searchButton: {
        height: 56,
        marginLeft: theme.spacing(1.5),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: '10px 20px',
        minWidth: 150,
        flex: '0 0 150px',
        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
        transition: 'all 0.3s',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.5)',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(33, 150, 243, 0.3)',
        },
    },
    tableContainer: {
        backgroundColor: '#252530',
        borderRadius: 8,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        overflow: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
            height: 8,
            backgroundColor: '#1e1e28',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#444',
            borderRadius: 4,
        },
    },
    tableCell: {
        color: '#fff',
        borderBottomColor: '#333',
        padding: '16px 12px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    tableHeaderCell: {
        color: '#9b9b9b',
        fontWeight: 500,
        letterSpacing: '1.2px',
        fontSize: '14px',
        borderBottom: '2px solid #333',
        padding: '16px 24px',
        backgroundColor: '#1e1e28',
    },
    actionColumn: {
        position: 'sticky',
        right: 0,
        backgroundColor: '#1e1e28',
        boxShadow: '-5px 0 5px rgba(0, 0, 0, 0.2)',
        zIndex: 2,
    },
    actionColumnHeader: {
        position: 'sticky',
        right: 0,
        backgroundColor: '#1e1e28',
        boxShadow: '-5px 0 5px rgba(0, 0, 0, 0.2)',
        zIndex: 3,
    },
    table: {
        tableLayout: 'fixed',
        minWidth: 1200,
    },
    tableRow: {
        transition: 'background-color 0.2s',
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
        },
    },
    errorMessage: {
        color: '#ff5a5f',
        margin: theme.spacing(2, 0),
        padding: theme.spacing(1.5, 2),
        backgroundColor: 'rgba(255, 90, 95, 0.1)',
        borderRadius: theme.shape.borderRadius,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(255, 90, 95, 0.2)',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        },
    },
    noData: {
        textAlign: 'center',
        padding: '40px 0',
        color: '#999',
        backgroundColor: '#252530',
        borderRadius: 8,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    },
    subscriptionDetails: {
        backgroundColor: '#252530',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
        width: '100%',
        animation: '$fadeIn 0.4s ease-out',
    },
    subscriptionTitle: {
        color: '#fff',
        marginBottom: theme.spacing(2),
        fontSize: 22,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1.5),
            color: theme.palette.primary.main,
        },
    },
    statsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    statCard: {
        backgroundColor: '#1e1e28',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        flex: '1 1 200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    statValue: {
        color: theme.palette.primary.main,
        fontSize: '24px',
        fontWeight: 'bold',
        marginTop: theme.spacing(1),
    },
    statLabel: {
        color: '#9b9b9b',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    statusChip: {
        margin: theme.spacing(0, 1, 1, 0),
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
    },
    activeStatus: {
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        color: '#4caf50',
    },
    expiredStatus: {
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        color: '#f44336',
    },
    tablePagination: {
        color: '#fff',
        '& .MuiIconButton-root': {
            color: '#fff',
        },
        '& .MuiSelect-icon': {
            color: '#fff',
        }
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(10px)'
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)'
        }
    },
    description: {
        color: '#aaa',
        fontSize: '14px',
        marginBottom: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            color: '#888',
        },
    },
    detailButton: {
        minWidth: 'auto',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        color: theme.palette.primary.main,
        padding: '6px',
        borderRadius: '50%',
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            boxShadow: '0 2px 6px rgba(33, 150, 243, 0.3)',
        },
        transition: 'all 0.2s ease-in-out',
    },
    dialogTitle: {
        backgroundColor: '#252530',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2),
        '& h2': {
            fontSize: '1.5rem',
            fontWeight: 500
        }
    },
    dialogContent: {
        backgroundColor: '#1e1e28',
        color: '#fff',
        padding: theme.spacing(3),
        minWidth: 550,
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto'
    },
    closeButton: {
        color: '#aaa',
        '&:hover': {
            color: '#fff',
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
    },
    alertContainer: {
        marginBottom: theme.spacing(3),
    },
    actionButtonsContainer: {
        marginBottom: theme.spacing(3),
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    actionButton: {
        flex: '1 1 250px',
        padding: theme.spacing(2.5),
        borderRadius: 12,
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: 'none',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
        },
        '&:hover::after': {
            opacity: 1,
        },
    },
    refreshButton: {
        background: 'linear-gradient(45deg, #1565C0 30%, #2196F3 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #0D47A1 30%, #1565C0 90%)',
        },
    },
    cancelButton: {
        background: 'linear-gradient(45deg, #FF8F00 30%, #FFC107 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #F57C00 30%, #FF8F00 90%)',
        },
    },
    addButton: {
        background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #1B5E20 30%, #2E7D32 90%)',
        },
    },
    buttonIcon: {
        marginRight: theme.spacing(2.5),
        fontSize: '2rem',
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))',
    },
    buttonContent: {
        textAlign: 'left',
    },
    buttonTitle: {
        fontWeight: 'bold',
        marginBottom: 4,
    },
    buttonDescription: {
        fontSize: '0.75rem',
        opacity: 0.9,
    },
    chipActive: {
        backgroundColor: 'rgba(76, 175, 80, 0.15)',
        color: '#4caf50',
        fontWeight: 500,
    },
    chipInactive: {
        backgroundColor: 'rgba(244, 67, 54, 0.15)',
        color: '#f44336',
        fontWeight: 500,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#252530',
            color: '#fff',
        },
        '& .MuiInputLabel-outlined': {
            color: '#aaa',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#444',
        },
        '& .MuiSelect-icon': {
            color: '#fff',
        },
    },
    // 添加到 useStyles 中
    detailItem: {
        display: 'flex',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        padding: theme.spacing(1, 0),
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    detailLabel: {
        color: '#9b9b9b',
        width: '120px',
        fontWeight: 500,
    },
    detailValue: {
        color: '#fff',
        flex: 1,
    },
    // cancelButtonTable: {
    //     background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
    //     color: 'white',
    //     '&:hover': {
    //         background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
    //         boxShadow: '0 3px 10px rgba(244, 67, 54, 0.3)',
    //     },
    // },
    actionButtonGroup: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    tableIconButton: {
        padding: 8,
        color: theme.palette.primary.main,
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            boxShadow: '0 2px 6px rgba(33, 150, 243, 0.3)',
        },
    },
}));

// 创建表头单元格组件
const TableHeaderCell = ({ children, ...props }) => {
    const classes = useStyles();
    return (
        <TableCell className={classes.tableHeaderCell} {...props}>
            {children}
        </TableCell>
    );
};

function SubscriptionManagement(props) {
    const classes = useStyles();
    const [searchQuery, setSearchQuery] = useState('');
    const [subscriptionType, setSubscriptionType] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        totalSubscriptions: 0,
        activeSubscriptions: 0,
        expiredSubscriptions: 0
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [addUserId, setAddUserId] = useState('');
    // 添加以下状态用于进度跟踪
    const [batchProgress, setBatchProgress] = useState({
        current: 0,
        total: 0,
        currentUser: null,
        success: 0,
        failed: 0,
        showProgress: false,
        cancelling: false
    });
    const [isProcessing, setIsProcessing] = useState({
        refresh: false,
        cancelAll: false,
        addSubscription: false
    });
    const [actionAlert, setActionAlert] = useState({
        show: false,
        severity: 'success',
        message: ''
    });
    const [permissionsList, setPermissionsList] = useState([]);

    // 添加详情对话框状态和处理函数
    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);

    // 处理查看详情按钮点击
    const handleViewDetails = (subscription) => {
        setSelectedSubscription(subscription);
        setOpenDetailsDialog(true);
    };

    // 处理关闭详情对话框
    const handleCloseDetailsDialog = () => {
        setOpenDetailsDialog(false);
        setSelectedSubscription(null);
    };

    const [addSubscriptionTypes, setAddSubscriptionTypes] = useState({});

    // 在 handleOpenAddDialog 中设置默认所有权限都被选中
    const handleOpenAddDialog = () => {
        setAddUserId('');
        // 初始化一个对象，将所有权限都设置为选中状态
        const initialTypes = {};
        permissionsList.forEach(permission => {
            initialTypes[permission] = true;
        });
        setAddSubscriptionTypes(initialTypes);
        setOpenAddDialog(true);
    };

    // 添加处理权限选择变更的函数
    const handleSubscriptionTypeChange = (event) => {
        const { name, checked } = event.target;
        setAddSubscriptionTypes(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    // 修改添加订阅的处理函数，使用多个权限
    const handleAddSubscription = async () => {
        if (!addUserId) {
            setActionAlert({
                show: true,
                severity: 'error',
                message: '请输入有效的用户ID'
            });
            return;
        }

        // 检查是否至少选择了一项权限
        const selectedTypes = Object.keys(addSubscriptionTypes).filter(key => addSubscriptionTypes[key]);
        if (selectedTypes.length === 0) {
            setActionAlert({
                show: true,
                severity: 'error',
                message: '请至少选择一种订阅类型'
            });
            return;
        }

        setIsProcessing(prev => ({ ...prev, addSubscription: true }));

        try {
            // 这里可以发送请求给后端
            // 在实际应用中调用添加订阅的API
            const { token } = props.auth;
            const config = {
                headers: { Authorization: `JWT ${token}` },
                params: {
                    broadcasterId: addUserId,
                    // 可以根据需要添加选中的权限类型参数
                    subscriptionTypes: selectedTypes.join(',')
                }
            };

            // 调用创建订阅的API
            const response = await axios.get('/api/v1/twitchEventsubsig', config);
            if (response.status === 200) {
                // 关闭对话框并显示成功消息
                handleCloseAddDialog();
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `已成功为用户 ${addUserId} 添加订阅`
                });

                // 刷新订阅列表
                fetchSubscriptions();
            } else {
                throw new Error('服务器返回了意外的状态码: ' + response.status);
            }
        } catch (err) {
            console.error('添加订阅失败:', err);

            let errorMessage = '添加订阅失败';
            if (err.response && err.response.data && err.response.data.message) {
                errorMessage += ': ' + err.response.data.message;
            } else if (err.message) {
                errorMessage += ': ' + err.message;
            }

            setActionAlert({
                show: true,
                severity: 'error',
                message: errorMessage
            });
        } finally {
            setIsProcessing(prev => ({ ...prev, addSubscription: false }));
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };

    // 添加详情对话框组件
    const renderDetailsDialog = () => {
        if (!selectedSubscription) return null;

        return (
            <Dialog
                open={openDetailsDialog}
                onClose={handleCloseDetailsDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Twitch订阅详情 - {selectedSubscription.displayName}
                        </Typography>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleCloseDetailsDialog}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16, height: '100%' }}>
                                <Typography variant="subtitle1" gutterBottom style={{ color: '#2196f3' }}>
                                    用户信息
                                </Typography>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>Twitch ID:</Typography>
                                    <Typography className={classes.detailValue}>{selectedSubscription.userId}</Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>用户名:</Typography>
                                    <Typography className={classes.detailValue}>{selectedSubscription.username}</Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>Twitch用户名:</Typography>
                                    <Typography className={classes.detailValue}>{selectedSubscription.displayName}</Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>邮箱:</Typography>
                                    <Typography className={classes.detailValue}>{selectedSubscription.email}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16, height: '100%' }}>
                                <Typography variant="subtitle1" gutterBottom style={{ color: '#2196f3' }}>
                                    订阅统计
                                </Typography>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>授权状态:</Typography>
                                    <Typography className={classes.detailValue}>
                                        <Chip
                                            label={selectedSubscription.subscriptionStatus}
                                            className={selectedSubscription.subscriptionStatus === '完整' ? classes.chipActive : classes.chipInactive}
                                            size="small"
                                        />
                                    </Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>订阅数量:</Typography>
                                    <Typography className={classes.detailValue}>{selectedSubscription.subscriptionCount}</Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>首次订阅:</Typography>
                                    <Typography className={classes.detailValue}>{formatDate(selectedSubscription.startDate)}</Typography>
                                </Box>
                                <Box className={classes.detailItem}>
                                    <Typography className={classes.detailLabel}>最近订阅:</Typography>
                                    <Typography className={classes.detailValue}>{formatDate(selectedSubscription.endDate)}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16 }}>
                                <Typography variant="subtitle1" gutterBottom style={{ color: '#2196f3' }}>
                                    Client IDs ({selectedSubscription.clientIds?.length || 0})
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedSubscription.clientIds?.map((clientId, index) => (
                                        <Chip
                                            key={index}
                                            label={clientId}
                                            size="small"
                                            style={{
                                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                color: '#2196f3',
                                                margin: '2px'
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16 }}>
                                <Typography variant="subtitle1" gutterBottom style={{ color: '#2196f3' }}>
                                    已授权事件类型 ({selectedSubscription.authorizedEventTypes?.length || 0})
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedSubscription.authorizedEventTypes?.map((eventType, index) => (
                                        <Chip
                                            key={index}
                                            label={eventType}
                                            size="small"
                                            style={{
                                                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                                color: '#4caf50',
                                                margin: '2px'
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16 }}>
                                <Typography variant="subtitle1" gutterBottom style={{ color: '#2196f3', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>订阅详情 ({selectedSubscription.subscriptions?.length || 0})</span>
                                </Typography>
                                {selectedSubscription.subscriptions?.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ color: '#9b9b9b', borderBottom: '1px solid #333' }}>事件类型</TableCell>
                                                    <TableCell style={{ color: '#9b9b9b', borderBottom: '1px solid #333' }}>订阅ID</TableCell>
                                                    <TableCell style={{ color: '#9b9b9b', borderBottom: '1px solid #333' }}>状态</TableCell>
                                                    <TableCell style={{ color: '#9b9b9b', borderBottom: '1px solid #333' }}>创建时间</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedSubscription.subscriptions.map((sub, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell style={{ color: '#fff', borderBottom: '1px solid #333' }}>{sub.eventType}</TableCell>
                                                        <TableCell style={{ color: '#fff', borderBottom: '1px solid #333' }}>
                                                            <Tooltip title={sub.subscriptionId}>
                                                                <span>{sub.subscriptionId.substring(0, 10)}...</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell style={{ color: '#fff', borderBottom: '1px solid #333' }}>
                                                            <Chip
                                                                label={sub.status}
                                                                size="small"
                                                                style={{
                                                                    backgroundColor: sub.status === 'enabled' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                                                    color: sub.status === 'enabled' ? '#4caf50' : '#f44336'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell style={{ color: '#fff', borderBottom: '1px solid #333' }}>{formatDate(sub.createdAt)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" align="center" style={{ padding: '16px 0' }}>
                                        没有详细的订阅数据
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                    <Button
                        onClick={() => {
                            handleCancelSubscription(selectedSubscription);
                            handleCloseDetailsDialog();
                        }}
                        color="secondary"
                        variant="contained"
                        disabled={!selectedSubscription.isActive}
                        startIcon={<DeleteIcon />}
                        style={{ marginRight: 'auto' }}
                    >
                        取消订阅
                    </Button>
                    <Button onClick={handleCloseDetailsDialog} color="primary" variant="contained">
                        关闭
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 模拟数据 - 实际应用中应该从API获取
    useEffect(() => {
        // 组件加载时获取数据
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        setIsLoading(true);
        setError('');

        try {
            const { token } = props.auth;
            const config = { headers: { Authorization: `JWT ${token}` } };
            const response = await axios.get('/api/v1/fetchUserstwitchEvents', config);

            if (response.data && response.data.status === 'success') {
                // 处理用户列表数据
                const transformedData = response.data.data.usersList.map(user => ({
                    id: user.twitchId,
                    userId: user.twitchId,
                    username: user.username || '未知用户名',
                    displayName: user.twitchUsername || user.username || '未知用户',
                    email: user.email || '-',
                    subscriptionType: user.isFullyAuthorized ? 'premium' : 'standard',
                    startDate: user.firstSubscriptionDate,
                    endDate: user.lastSubscriptionDate,
                    isActive: true, // 假设所有返回的订阅都是活跃的
                    authorizedEventTypes: user.authorizedEventTypes || [],
                    subscriptionCount: user.subscriptionCount || 0,
                    subscriptionStatus: user.subscriptionStatus || '-',
                    avatar: user.avatar,
                    subscriptions: user.subscriptions || [],
                    clientIds: user.clientIds || []
                }));

                setSubscriptions(transformedData);

                // 更新统计数据
                const statistics = response.data.data.statistics;
                setStats({
                    totalSubscriptions: statistics.totalSubscriptions || 0,
                    activeSubscriptions: statistics.uniqueBroadcasters || 0,
                    expiredSubscriptions: 0, // API 不提供过期订阅信息
                    fullyAuthorizedUsers: statistics.fullyAuthorizedUsers || 0,
                    partiallyAuthorizedUsers: statistics.partiallyAuthorizedUsers || 0,
                    uniqueClientIds: statistics.uniqueClientIds || 0
                });

                // 保存可用的权限列表
                setPermissionsList(response.data.data.permissionsList || []);

                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: '订阅数据已成功加载'
                });
            } else {
                throw new Error(response.data?.error || '获取数据失败');
            }
        } catch (err) {
            console.error('加载订阅数据失败:', err);
            setError('无法加载订阅数据，请稍后再试');
            setActionAlert({
                show: true,
                severity: 'error',
                message: '加载数据失败: ' + (err.response?.data?.error || err.message || '未知错误')
            });
        } finally {
            setIsLoading(false);
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleTypeChange = (e) => {
        setSubscriptionType(e.target.value);
    };

    const handleSearch = () => {
        fetchSubscriptions();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            fetchSubscriptions();
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
    };

    const handleRefreshSubscriptions = async () => {
        setIsProcessing(prev => ({ ...prev, refresh: true }));

        try {
            // 调用刷新订阅状态的API
            const { token } = props.auth;
            const config = { headers: { Authorization: `JWT ${token}` } };
            const response = await axios.get('/api/v1/syncSubscriptions', config);

            // 检查API返回结果
            if (response.data && response.data.success === true && response.data.message === "订阅同步完成") {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: response.data.message
                });

                // 刷新订阅列表
                fetchSubscriptions();
            } else {
                throw new Error(response.data?.message || '更新订阅状态失败');
            }
        } catch (err) {
            console.error('刷新订阅状态失败:', err);
            setActionAlert({
                show: true,
                severity: 'error',
                message: '刷新订阅状态失败: ' + (err.response?.data?.message || err.message || '未知错误')
            });
        } finally {
            setIsProcessing(prev => ({ ...prev, refresh: false }));
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };

    // 重新实现批量取消订阅方法
    const handleCancelAllSubscriptions = async () => {
        // 确认对话框
        if (!window.confirm('警告：此操作将逐个取消所有用户的订阅。是否确定继续？')) {
            return;
        }

        // 获取活跃用户
        const activeSubscriptions = subscriptions.filter(sub => sub.isActive);
        if (activeSubscriptions.length === 0) {
            setActionAlert({
                show: true,
                severity: 'info',
                message: '没有可取消的活跃订阅'
            });
            return;
        }

        // 设置进度状态
        setBatchProgress({
            current: 0,
            total: activeSubscriptions.length,
            currentUser: null,
            success: 0,
            failed: 0,
            showProgress: true,
            cancelling: true
        });

        // 为所有订阅设置标记以防止状态更新错误
        let cancelled = false;
        // Track counts locally — reading batchProgress after the loop sees the stale closure value
        // (always the pre-loop 0/0), so the summary message and severity must come from these.
        let successCount = 0;
        let failedCount = 0;

        // 逐个处理用户
        for (let i = 0; i < activeSubscriptions.length; i++) {
            // 如果操作被取消，退出循环
            if (cancelled) break;

            const subscription = activeSubscriptions[i];

            // 更新当前处理的用户和进度
            setBatchProgress(prev => ({
                ...prev,
                current: i + 1,
                currentUser: subscription
            }));

            try {
                // 获取 broadcasterId
                const broadcasterId = subscription.userId;

                if (!broadcasterId) {
                    throw new Error('找不到有效的 Twitch ID');
                }

                // 获取认证令牌
                const { token } = props.auth;
                const config = {
                    headers: { Authorization: `JWT ${token}` },
                    params: { broadcasterId }
                };

                // 调用取消订阅的 API
                const response = await axios.get('/api/v1/twitchEventsubsigRev', config);

                if (response.status === 200) {
                    // 更新成功计数
                    successCount += 1;
                    setBatchProgress(prev => ({
                        ...prev,
                        success: prev.success + 1
                    }));

                    // 从本地数据中移除已取消的订阅
                    setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
                } else {
                    throw new Error('服务器返回了意外的状态码: ' + response.status);
                }
            } catch (err) {
                console.error(`取消用户 ${subscription.displayName || subscription.username} 订阅失败:`, err);

                // 更新失败计数
                failedCount += 1;
                setBatchProgress(prev => ({
                    ...prev,
                    failed: prev.failed + 1
                }));
            }

            // 等待一小段时间，避免请求过快
            await new Promise(resolve => setTimeout(resolve, 300));
        }

        // 完成所有处理后
        if (!cancelled) {
            setBatchProgress(prev => ({
                ...prev,
                cancelling: false
            }));

            // 显示结果消息（使用本地计数，避免闭包中的 batchProgress 过期）
            setActionAlert({
                show: true,
                severity: failedCount > 0 ? (successCount > 0 ? 'warning' : 'error') : 'success',
                message: `批量取消订阅完成：成功 ${successCount} 个，失败 ${failedCount} 个`
            });

            // 刷新订阅列表
            await fetchSubscriptions();
        }
    };

    // 添加进度对话框组件
    const renderProgressDialog = () => {
        return (
            <Dialog
                open={batchProgress.showProgress}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            批量取消订阅
                        </Typography>
                        <IconButton
                            className={classes.closeButton}
                            onClick={() => {
                                if (!batchProgress.cancelling) {
                                    setBatchProgress(prev => ({ ...prev, showProgress: false }));
                                }
                            }}
                            size="small"
                            disabled={batchProgress.cancelling}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            正在批量取消用户订阅
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {batchProgress.cancelling ? (
                                `正在处理中 (${batchProgress.current}/${batchProgress.total})`
                            ) : (
                                `处理完成：成功 ${batchProgress.success} 个，失败 ${batchProgress.failed} 个`
                            )}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <LinearProgress
                            variant="determinate"
                            value={(batchProgress.current / batchProgress.total) * 100}
                            style={{ height: 10, borderRadius: 5 }}
                        />
                    </Box>

                    {batchProgress.currentUser && (
                        <Box mb={3} p={2} bgcolor="#252530" borderRadius={1}>
                            <Typography variant="subtitle2" gutterBottom>
                                当前处理：
                            </Typography>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="body2" style={{ width: 100, color: '#9b9b9b' }}>
                                    用户名：
                                </Typography>
                                <Typography variant="body2">
                                    {batchProgress.currentUser.displayName || batchProgress.currentUser.username}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" style={{ width: 100, color: '#9b9b9b' }}>
                                    Twitch ID：
                                </Typography>
                                <Typography variant="body2">
                                    {batchProgress.currentUser.userId}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Box>
                            <Typography variant="body2" style={{ color: '#4caf50' }}>
                                成功：{batchProgress.success}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" style={{ color: '#f44336' }}>
                                失败：{batchProgress.failed}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                    {batchProgress.cancelling ? (
                        <Button
                            onClick={handleCancelOperation}
                            color="secondary"
                            variant="contained"
                        >
                            停止操作
                        </Button>
                    ) : (
                        <Button
                            onClick={() => setBatchProgress(prev => ({ ...prev, showProgress: false }))}
                            color="primary"
                            variant="contained"
                        >
                            关闭
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    };

    // 添加取消批量操作的处理函数
    const handleCancelOperation = () => {
        setBatchProgress(prev => ({
            ...prev,
            cancelling: false
        }));
    };

    // 修改取消单个订阅的逻辑
    const handleCancelSubscription = async (subscription) => {
        if (!window.confirm(`确定要取消用户 ${subscription.displayName || subscription.username} 的订阅吗？`)) {
            return;
        }

        try {
            setIsLoading(true);

            // 从订阅对象中获取 broadcasterId (twitchId)
            const broadcasterId = subscription.userId;

            if (!broadcasterId) {
                throw new Error('找不到有效的 Twitch ID');
            }

            // 获取认证令牌
            const { token } = props.auth;
            const config = {
                headers: { Authorization: `JWT ${token}` },
                params: { broadcasterId }
            };

            // 调用取消订阅的 API
            const response = await axios.get('/api/v1/twitchEventsubsigRev', config);

            if (response.status === 200) {
                setActionAlert({
                    show: true,
                    severity: 'success',
                    message: `已成功取消 ${subscription.displayName || subscription.username} 的订阅`
                });

                // 从本地数据中移除已取消的订阅
                setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
            } else {
                throw new Error('服务器返回了意外的状态码: ' + response.status);
            }
        } catch (err) {
            console.error('取消订阅失败:', err);

            let errorMessage = '取消订阅失败';
            if (err.response && err.response.data && err.response.data.message) {
                errorMessage += ': ' + err.response.data.message;
            } else if (err.message) {
                errorMessage += ': ' + err.message;
            }

            setActionAlert({
                show: true,
                severity: 'error',
                message: errorMessage
            });
        } finally {
            setIsLoading(false);
            // 5秒后自动隐藏提示
            setTimeout(() => setActionAlert(prev => ({ ...prev, show: false })), 5000);
        }
    };


    // 修改过滤订阅数据部分的类型筛选逻辑
    const filteredSubscriptions = subscriptions.filter(sub => {
        // 按类型筛选
        if (subscriptionType !== 'all' && !(sub.authorizedEventTypes || []).includes(subscriptionType)) {
            return false;
        }

        // 按搜索词筛选
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                (sub.username && sub.username.toLowerCase().includes(query)) ||
                (sub.displayName && sub.displayName.toLowerCase().includes(query)) ||
                (sub.email && sub.email.toLowerCase().includes(query)) ||
                (sub.userId && sub.userId.toLowerCase().includes(query))
            );
        }

        return true;
    });

    // 分页
    const paginatedSubscriptions = filteredSubscriptions
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // 格式化日期函数
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Container maxWidth="xl">
            <Box className={classes.container}>
                <Typography variant="h4" gutterBottom>权限订阅管理</Typography>
                <Typography className={classes.description}>
                    <InfoOutlinedIcon />
                    管理用户的订阅状态，查看当前订阅列表并进行添加/取消操作
                </Typography>

                {/* 操作提示框 */}
                {actionAlert.show && (
                    <Box className={classes.alertContainer}>
                        <Alert
                            severity={actionAlert.severity}
                            variant="filled"
                            onClose={() => setActionAlert(prev => ({ ...prev, show: false }))}
                        >
                            {actionAlert.message}
                        </Alert>
                    </Box>
                )}

                {/* 操作按钮区 */}
                <Box className={classes.actionButtonsContainer}>
                    <Button
                        className={`${classes.actionButton} ${classes.refreshButton}`}
                        onClick={handleRefreshSubscriptions}
                        disabled={isProcessing.refresh}
                        startIcon={isProcessing.refresh ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                更新订阅状态
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                同步所有用户的最新订阅状态
                            </Typography>
                        </Box>
                    </Button>

                    <Button
                        className={`${classes.actionButton} ${classes.addButton}`}
                        onClick={handleOpenAddDialog}
                        disabled={isProcessing.addSubscription}
                        startIcon={<PersonAddIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                添加新订阅
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                为指定用户添加订阅权限
                            </Typography>
                        </Box>
                    </Button>

                    <Button
                        className={`${classes.actionButton} ${classes.cancelButton}`}
                        onClick={handleCancelAllSubscriptions}
                        disabled={isProcessing.cancelAll}
                        startIcon={isProcessing.cancelAll ? <CircularProgress size={24} color="inherit" /> : <DeleteIcon className={classes.buttonIcon} />}
                    >
                        <Box className={classes.buttonContent}>
                            <Typography variant="subtitle1" className={classes.buttonTitle}>
                                取消所有订阅
                            </Typography>
                            <Typography variant="body2" className={classes.buttonDescription}>
                                批量取消所有活跃的用户订阅
                            </Typography>
                        </Box>
                    </Button>
                </Box>

                {/* 统计卡片 */}
                <Card className={classes.subscriptionDetails} elevation={3}>
                    <CardContent>
                        <Typography variant="h6" className={classes.subscriptionTitle}>
                            <InfoIcon style={{ color: '#2196f3' }} />
                            订阅概览
                        </Typography>

                        <div className={classes.statsContainer}>
                            <div className={classes.statCard}>
                                <InfoIcon color="primary" />
                                <div className={classes.statValue}>{stats.totalSubscriptions}</div>
                                <div className={classes.statLabel}>总订阅数</div>
                            </div>
                            <div className={classes.statCard}>
                                <CheckCircleIcon style={{ color: '#4caf50' }} />
                                <div className={classes.statValue} style={{ color: '#4caf50' }}>{stats.fullyAuthorizedUsers}</div>
                                <div className={classes.statLabel}>完整授权用户</div>
                            </div>
                            <div className={classes.statCard}>
                                <ErrorIcon style={{ color: '#f44336' }} />
                                <div className={classes.statValue} style={{ color: '#f44336' }}>{stats.partiallyAuthorizedUsers}</div>
                                <div className={classes.statLabel}>部分授权用户</div>
                            </div>
                            <div className={classes.statCard}>
                                <CodeIcon style={{ color: '#ff9800' }} />
                                <div className={classes.statValue} style={{ color: '#ff9800' }}>{stats.uniqueClientIds}</div>
                                <div className={classes.statLabel}>唯一客户端ID</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 搜索表单 */}
                <Box className={classes.searchForm}>
                    <TextField
                        className={classes.searchField}
                        label="搜索用户名、邮箱或用户ID"
                        variant="outlined"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyPress={handleKeyPress}
                        placeholder="输入关键词搜索..."
                    />

                    <FormControl variant="outlined" className={classes.formControl}>
                        <Select
                            value={subscriptionType}
                            onChange={handleTypeChange}
                            displayEmpty
                            className={classes.selectEmpty}
                        >
                            <MenuItem value="all">所有类型</MenuItem>
                            {permissionsList.map(permission => (
                                <MenuItem key={permission} value={permission}>{permission}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        className={classes.searchButton}
                        variant="contained"
                        onClick={handleSearch}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    >
                        {isLoading ? '搜索中...' : '搜索订阅'}
                    </Button>
                </Box>

                {/* 错误提示 */}
                {error && (
                    <Box className={classes.errorMessage}>
                        <ErrorIcon /> {error}
                    </Box>
                )}

                {/* 订阅表格 */}
                {paginatedSubscriptions.length > 0 ? (
                    <>
                        <TableContainer component={Paper} className={classes.tableContainer}>
                            <Table className={classes.table} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell width="180px">Twitch ID</TableHeaderCell>
                                        <TableHeaderCell width="150px">用户名</TableHeaderCell>
                                        <TableHeaderCell width="200px">Twitch用户名</TableHeaderCell>
                                        <TableHeaderCell width="220px">邮箱</TableHeaderCell>
                                        <TableHeaderCell width="120px">授权状态</TableHeaderCell>
                                        <TableHeaderCell width="100px">订阅数</TableHeaderCell>
                                        <TableHeaderCell width="180px">首次订阅时间</TableHeaderCell>
                                        <TableHeaderCell width="180px">最近订阅时间</TableHeaderCell>
                                        <TableHeaderCell width="80px" align="center" className={classes.actionColumnHeader}>
                                            操作
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedSubscriptions.map((subscription) => (
                                        <TableRow key={subscription.id} className={classes.tableRow}>
                                            <TableCell className={classes.tableCell}>
                                                <Tooltip title={subscription.userId} placement="top">
                                                    <span>{subscription.userId.substring(0, 12)}...</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {subscription.username}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {subscription.displayName || '-'}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {subscription.email}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Chip
                                                    label={subscription.subscriptionStatus}
                                                    className={subscription.subscriptionStatus === '完整' ? classes.chipActive : classes.chipInactive}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Tooltip title={`${subscription.subscriptionCount} 个事件类型`} placement="top">
                                                    <span>{subscription.subscriptionCount}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {formatDate(subscription.startDate)}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {formatDate(subscription.endDate)}
                                            </TableCell>
                                            <TableCell className={`${classes.tableCell} ${classes.actionColumn}`} align="center">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleViewDetails(subscription)}
                                                    className={classes.tableIconButton}
                                                    title="查看详情"
                                                >
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={filteredSubscriptions.length}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            className={classes.tablePagination}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            labelRowsPerPage="每页行数:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count}`}
                        />
                    </>
                ) : (
                    <Box className={classes.noData}>
                        <Typography variant="body1">暂无订阅数据</Typography>
                    </Box>
                )}

                {/* 添加订阅对话框 */}
                <Dialog
                    open={openAddDialog}
                    onClose={handleCloseAddDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        style: {
                            backgroundColor: '#1e1e28',
                            borderRadius: 8,
                        },
                    }}
                >
                    <DialogTitle className={classes.dialogTitle}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                                添加新订阅
                            </Typography>
                            <IconButton
                                className={classes.closeButton}
                                onClick={handleCloseAddDialog}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent className={classes.dialogContent}>
                        <Box mb={2}>
                            <Typography variant="body1" gutterBottom>
                                为用户添加订阅权限
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                请输入用户ID并选择需要订阅的权限类型
                            </Typography>
                        </Box>

                        <TextField
                            autoFocus
                            margin="dense"
                            id="userId"
                            label="用户ID"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={addUserId}
                            onChange={(e) => setAddUserId(e.target.value)}
                            className={classes.searchField}
                            style={{ marginBottom: '16px' }}
                        />

                        <Box mt={2}>
                            <InputLabel id="subscription-type-label" style={{ color: '#aaa', marginBottom: '8px' }}>
                                订阅类型（默认全选）
                            </InputLabel>
                            <Paper elevation={0} style={{ backgroundColor: '#252530', padding: 16, maxHeight: '300px', overflow: 'auto' }}>
                                <FormGroup>
                                    {permissionsList.map(permission => (
                                        <FormControlLabel
                                            key={permission}
                                            control={
                                                <Checkbox
                                                    checked={addSubscriptionTypes[permission] || false}
                                                    onChange={handleSubscriptionTypeChange}
                                                    name={permission}
                                                    color="primary"
                                                />
                                            }
                                            label={permission}
                                            style={{ color: '#fff', marginBottom: '8px' }}
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>

                            <Box display="flex" justifyContent="flex-end" mt={1}>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                        const allChecked = permissionsList.every(p => addSubscriptionTypes[p]);
                                        const newState = {};
                                        permissionsList.forEach(p => {
                                            newState[p] = !allChecked;
                                        });
                                        setAddSubscriptionTypes(newState);
                                    }}
                                >
                                    {permissionsList.every(p => addSubscriptionTypes[p]) ? '取消全选' : '全选'}
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ backgroundColor: '#1e1e28', padding: '16px 24px' }}>
                        <Button
                            onClick={handleCloseAddDialog}
                            color="default"
                            disabled={isProcessing.addSubscription}
                        >
                            取消
                        </Button>
                        <Button
                            onClick={handleAddSubscription}
                            color="primary"
                            variant="contained"
                            disabled={isProcessing.addSubscription || Object.keys(addSubscriptionTypes).filter(k => addSubscriptionTypes[k]).length === 0}
                            startIcon={isProcessing.addSubscription ? <CircularProgress size={20} /> : null}
                        >
                            {isProcessing.addSubscription ? '处理中...' : '添加订阅'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>

            {/* 详情对话框 */}
            {renderDetailsDialog()}

            {/* 批量处理进度对话框 */}
            {renderProgressDialog()}
        </Container>
    );
}

export default SubscriptionManagement;